import {
  useVehicleFilterReducedDetail,
  useVehiclesFilterReduced,
} from '@/core/domain/people/Customers/Customers.hooks';
import {
  CustomerVehicleReduced,
  CustomerVehiclesFilterParamsReduced,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { useDebounce } from '@/hooks';
import { SelectProps, Typography } from 'antd';
import { useEffect, useState } from 'react';
import InfiniteSelect from '../InfiniteSelect';
import useSelectOptions from '@/hooks/useSelectOptions';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { isValidPlate } from '@/utils/functions/isValidLicensePlate';
import { CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;

type Props = Omit<SelectProps, 'options'> & {
  customerId?: number;
  onVehicleSelect?: (vehicle?: CustomerVehicleReduced) => void;
  queryEnable?: boolean;
  setPlateWithVehicle?: boolean;
  queryParams?: CustomerVehiclesFilterParamsReduced;
};

const VehicleSelectField: React.FC<Props> = ({
  customerId,
  queryEnable = false,
  onVehicleSelect,
  setPlateWithVehicle,
  ...props
}) => {
  const [selectClicked, setSelectClicked] = useState<boolean>(queryEnable);
  const [searchPlate, setSearchPlate] = useState('');
  const licensePlate = useDebounce(searchPlate);
  const [hasClear, setHasClear] = useState(false);
  const [vehicleSelected, setVehicleSelected] =
    useState<CustomerVehicleReduced>();
  const [vehiclePlate, setVehiclePlate] = useState<CustomerVehicleReduced>();

  const isMultiple = props.mode === 'multiple';

  const vehiclesList = useVehiclesFilterReduced(
    {
      licensePlate,
      customerId,
      rows: 10,
      active: true,
      ...props.queryParams,
    },
    (selectClicked || queryEnable) && !props.disabled,
  );

  const vehicles = flatInfiniteData(vehiclesList.data);

  const vehicleDetail = useVehicleFilterReducedDetail();

  useEffect(() => {
    const configVehiclePlate =
      !customerId &&
      setPlateWithVehicle &&
      licensePlate &&
      !vehiclesList.isLoading &&
      (!vehiclePlate || licensePlate !== vehiclePlate.licensePlate);

    if (configVehiclePlate && isValidPlate(licensePlate) && !vehicles.length) {
      const item = { licensePlate, id: 0 } as CustomerVehicleReduced;
      setVehiclePlate(item);
    } else if (configVehiclePlate && !!vehicles.length) {
      setVehiclePlate(undefined);
    }
  }, [
    setPlateWithVehicle,
    licensePlate,
    vehicles,
    vehiclesList.isLoading,
    customerId,
    vehiclePlate,
  ]);

  const vehicleOptions = useSelectOptions({
    list: vehiclesList,
    detail: vehicleDetail,
    selectProps: props,
    labelFormatter: (item) =>
      item.brandName && item.modelName ? (
        <>
          <div>
            <Text>
              {item.brandName} - {item.modelName}
            </Text>
          </div>
          <div>
            <Text type="secondary">{item.licensePlate}</Text>
          </div>
        </>
      ) : (
        item.licensePlate
      ),
  });

  useEffect(() => {
    if (
      vehicleSelected &&
      typeof props.value !== 'undefined' &&
      vehicleSelected.id === props.value &&
      onVehicleSelect
    ) {
      onVehicleSelect(vehicleSelected);
    }
  }, [vehicleSelected, props.value]);

  useEffect(() => {
    if (
      vehicles.length === 1 &&
      !props.value &&
      props.onChange &&
      !hasClear &&
      licensePlate &&
      isValidPlate(licensePlate)
    ) {
      const vehicle = vehicles[0];

      const propValue = isMultiple ? [vehicle?.id] : vehicle.id;

      props.onChange(propValue, {
        value: vehicle.id,
        label: (
          <>
            <div>
              <Text>
                {vehicle.brandName} - {vehicle.modelName}
              </Text>
            </div>
            <div>
              <Text type="secondary">{vehicle.licensePlate}</Text>
            </div>
          </>
        ),
      });

      if (vehicleSelected?.id !== vehicle.id) {
        setVehicleSelected(vehicle);
      }
    }
  }, [
    vehiclesList.dataUpdatedAt,
    props.value,
    hasClear,
    vehicleSelected,
    licensePlate,
  ]);

  function handleClear() {
    setHasClear(true);
    props.onClear?.();
  }

  return (
    <InfiniteSelect
      fetchNextPage={vehiclesList.fetchNextPage}
      hasNextPage={vehiclesList.hasNextPage}
      loading={vehiclesList.isFetching}
      options={
        vehiclePlate
          ? [{ label: vehiclePlate.licensePlate, value: vehiclePlate.id }]
          : vehicleOptions
      }
      onSelect={(vehicleId: number) => {
        setSelectClicked(false);
        setHasClear(false);

        const selectedVehicle = vehicles.find(
          (vehicle) => vehicle.id === vehicleId,
        );

        if (!!selectedVehicle) {
          setVehicleSelected(selectedVehicle);
        } else if (setPlateWithVehicle && vehicleId === 0 && vehiclePlate) {
          setVehicleSelected({ ...vehiclePlate });
        }
      }}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      showSearch
      searchValue={searchPlate}
      filterOption={false}
      onSearch={(value) => {
        const plate = value.slice(0, 7).toUpperCase();
        setSearchPlate(plate);
      }}
      optionFilterProp="licensePlate"
      allowClear
      {...props}
      maxTagCount={isMultiple ? 'responsive' : undefined}
      maxTagTextLength={isMultiple ? 30 : undefined}
      {...(isMultiple && {
        tagRender: (tagProps) => {
          const { label, closable, onClose } = tagProps;

          const plate =
            vehicles.find((vehicle) => vehicle.id === tagProps.value)
              ?.licensePlate || label;

          return (
            <span className="ant-select-selection-item">
              <span className="ant-select-selection-item-content">{plate}</span>

              {closable && (
                <span
                  onClick={onClose}
                  className="ant-select-selection-item-remove"
                >
                  <CloseOutlined />
                </span>
              )}
            </span>
          );
        },
      })}
      onClear={handleClear}
    />
  );
};

export default VehicleSelectField;
